import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Greyscale, LocationIcon } from 'glints-aries';
import { isEmpty } from 'lodash';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { useDebounce } from 'use-debounce';

import { getDefaultSearchLocation } from 'src/common/constants/locations';
import { CountryCodes } from 'src/common/enums';
import { NewSearchLocation } from 'src/global/models/NewSearchLocation';
import { useLocationSuggestionsQuery } from 'src/modules/Opportunities/pages/Explore/Components/SearchSection/LocationSearchField/useLocationSuggestionsQuery';
import * as S from 'src/modules/Opportunities/pages/Explore/Components/SearchSection/SearchField.sc';

import { KEYS } from '../utils';

interface Props {
  locationSearchValue: NewSearchLocation;
  selectedCountry: CountryCodes;
  locationInputValue: string;
  onLocationInputChange: (inputValue: string) => void;
  locationResponse: NewSearchLocation[];
  onLocationClick: (value: React.SetStateAction<NewSearchLocation>) => void;
  isLocationError: boolean;
  isHomepage: boolean;
  suggestionLimit: number;
  onLocationResponseChange: (response: NewSearchLocation[]) => void;
  onLocationErrorChange: (value: React.SetStateAction<boolean>) => void;
  onEnter: () => void;
}

const LocationSearchFieldBase: React.FC<
  React.PropsWithChildren<
    Props & {
      forwadedRef: React.Ref<{ hideDropdown(): void }>;
    }
  >
> = ({
  forwadedRef,
  locationSearchValue,
  selectedCountry,
  locationInputValue,
  onLocationInputChange,
  onLocationErrorChange,
  locationResponse,
  onLocationClick,
  isLocationError,
  suggestionLimit,
  isHomepage,
  onLocationResponseChange,
  onEnter,
}) => {
  const intl = useIntl();
  const defaultSearch = getDefaultSearchLocation(selectedCountry);
  const translatedDefaultLabel = intl.formatMessage({
    id: 'location.all',
    defaultMessage: 'All Cities/Provinces',
  });

  const [openSuggestions, setOpenSuggestions] = useState<boolean>(false);
  const [selectionCursor, setSelectionCursor] = useState<number>(0);

  const onFocus = () => {
    setSelectionCursor(0);
    setOpenSuggestions(true);
  };
  const onBlur = () => {
    setOpenSuggestions(false);
    onLocationInputChange(
      locationSearchValue.label === defaultSearch.name
        ? ''
        : locationSearchValue.label
    );
    onLocationErrorChange(false);
  };
  const onMouseOver = (index: number) => setSelectionCursor(index);

  useEffect(() => {
    onLocationInputChange(translatedDefaultLabel);
  }, [translatedDefaultLabel, onLocationInputChange]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (locationResponse.length === 0) {
      return;
    }

    if (e.key === KEYS.ENTER) {
      if (locationResponse[selectionCursor]) {
        onLocationClick(locationResponse[selectionCursor]);
        onLocationErrorChange(false);
      }
      setSelectionCursor(0);
      setOpenSuggestions(false);
      onEnter();
      e.currentTarget.blur();
    } else if (
      e.key === KEYS.ARROW_DOWN &&
      selectionCursor < locationResponse.length - 1
    ) {
      setSelectionCursor(selectionCursor + 1);
    } else if (e.key === KEYS.ARROW_UP && selectionCursor > 0) {
      setSelectionCursor(selectionCursor - 1);
    }
  };

  useImperativeHandle(
    forwadedRef,
    () => {
      return {
        hideDropdown() {
          setOpenSuggestions(false);
        },
      };
    },
    []
  );

  /**
   * On focus: if default location is selected, clear the input so the
   * user can type immediately (the label acts as placeholder).
   * Always open suggestions when the input is empty or was just cleared.
   */
  const handleFocused = () => {
    if (locationSearchValue?.label === defaultSearch.name) {
      onLocationInputChange('');
      onFocus();
    } else if (!locationInputValue.length) {
      onFocus();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (typeof onLocationInputChange === 'function') {
      onLocationInputChange(e.target.value);
      onLocationErrorChange(true);
    }

    if (!openSuggestions) {
      setOpenSuggestions(true);
    }
  };

  const onSuggestionClick = (location: NewSearchLocation) => {
    onLocationClick(location);
    onLocationErrorChange(false);
    setOpenSuggestions(false);
  };

  const [searchTerm] = useDebounce(locationInputValue, 200);

  const searchTermForSelectedLocation = searchTerm.includes(
    locationSearchValue.name
  )
    ? locationSearchValue.name
    : searchTerm;

  const shouldShowL2 = (): boolean => {
    switch (selectedCountry) {
      case CountryCodes.ID:
      case CountryCodes.SG:
      case CountryCodes.MY:
        return true;
      case CountryCodes.VN:
      case CountryCodes.TW:
        return false;
    }
  };

  const { data: locationResultResponse } = useLocationSuggestionsQuery(
    searchTermForSelectedLocation,
    suggestionLimit,
    selectedCountry,
    { selectedLocation: locationSearchValue, shouldShowL2: shouldShowL2() }
  );

  const prevLocationResultRef = useRef<NewSearchLocation[]>();
  useEffect(() => {
    if (prevLocationResultRef.current === locationResultResponse) {
      return;
    }

    if (prevLocationResultRef.current && locationResultResponse) {
      // compare each item by type and name
      const isEqual =
        prevLocationResultRef.current.length ===
          locationResultResponse.length &&
        prevLocationResultRef.current.every(
          (item, index) =>
            item.type === locationResultResponse[index].type &&
            item.name === locationResultResponse[index].name
        );
      if (!isEqual) {
        prevLocationResultRef.current = locationResultResponse;
        onLocationResponseChange(locationResultResponse);
      }
    } else {
      prevLocationResultRef.current = locationResultResponse;
      onLocationResponseChange(locationResultResponse);
    }
  }, [locationResultResponse, onLocationResponseChange]);

  // Remove duplicates
  const selectedLocationResponse = locationResponse.filter(
    (respond) => respond.hierarchicalLocation?.name !== defaultSearch.name
  );

  return (
    <>
      <S.TextField
        type="text"
        label={
          locationSearchValue?.label === defaultSearch.name
            ? translatedDefaultLabel
            : intl.formatMessage(
                defineMessage({
                  id: 'text-add-a-city-province',
                  defaultMessage: 'Add a City/Province',
                })
              )
        }
        removeFloatingLabel={true}
        allowClear={true}
        value={
          locationInputValue === defaultSearch.name
            ? translatedDefaultLabel
            : locationInputValue
        }
        onChange={handleChange}
        onFocus={handleFocused}
        data-cy="search_bar_city"
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        startIcon={<LocationIcon />}
        isHomepage={isHomepage}
        isLocationError={isLocationError}
      />
      {openSuggestions && (
        <S.SuggestionDropdownContainer>
          <S.SuggestionDropdown>
            <Choose>
              <When condition={!isEmpty(locationResponse)}>
                <If condition={!locationInputValue.length}>
                  <S.SuggestedTitle>
                    <FormattedMessage
                      tagName="div"
                      id="title-suggested-location"
                      defaultMessage="Suggested Locations"
                    />
                  </S.SuggestedTitle>
                </If>
                {selectedLocationResponse.map(
                  (response: NewSearchLocation, index: number) => {
                    return (
                      <S.SearchItemWrapper
                        key={response.label}
                        className={index === selectionCursor && 'highlight'}
                        onClick={() => {
                          onSuggestionClick(response);
                        }}
                        // to prevent the blur call after mouse down. [https://stackoverflow.com/questions/17769005/onclick-and-onblur-ordering-issue]
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseOver={(e) => {
                          e.preventDefault();
                          onMouseOver(index);
                        }}
                      >
                        <LocationIcon
                          width="12px"
                          height="12px"
                          color={Greyscale.grey}
                        />
                        <S.SuggestionItem
                          onMouseOver={(e) => {
                            e.preventDefault();
                            onMouseOver(index);
                          }}
                        >
                          {response.label === defaultSearch.name
                            ? translatedDefaultLabel
                            : response.label}
                        </S.SuggestionItem>
                      </S.SearchItemWrapper>
                    );
                  }
                )}
              </When>
              <Otherwise>
                <S.NoLocationFoundWrapper>
                  <FormattedMessage
                    id="text-location-not-found"
                    defaultMessage="Location not found"
                    tagName="div"
                  />
                </S.NoLocationFoundWrapper>
              </Otherwise>
            </Choose>
          </S.SuggestionDropdown>
        </S.SuggestionDropdownContainer>
      )}
    </>
  );
};

const forwardRef = (
  props: Props,
  ref: React.RefObject<{ hideDropdown(): void }>
) => <LocationSearchFieldBase {...props} forwadedRef={ref} />;

export const LocationSearchField = React.forwardRef(forwardRef);
